import React, { FunctionComponent, ReactNode } from "react";
import { Box, useTheme } from "@mui/material";
import { tokens } from "../layout/theme";
import { BoxProps } from "@mui/system";

/**
 * Interface defining the props for the BoxWrap component.
 * Extends Material-UI's BoxProps for additional styling options.
 * 
 * @property {ReactNode} children - The child elements to render within the box
 * @property {string} [marginTop] - Custom margin-top value
 * @property {boolean} [hasBackground=true] - Whether to apply the theme background color
 */
interface BoxWrapProps extends BoxProps {
  children: ReactNode;
  marginTop?: string;
  hasBackground?: boolean;
}

/**
 * A reusable box wrapper component with theme integration and customizable styling.
 * 
 * This component provides a styled container with support for:
 * - Theme-based background color
 * - Rounded corners
 * - Custom margin spacing
 * - Optional background toggle
 * - All Material-UI Box props
 * 
 * @component
 * @param {BoxWrapProps} props - The props passed to the component
 * @param {ReactNode} props.children - Child elements
 * @param {string} [props.marginTop] - Top margin value
 * @param {boolean} [props.hasBackground=true] - Background toggle
 * @param {...BoxProps} props - Additional Material-UI Box props
 * 
 * @example
 * ```tsx
 * <BoxWrap marginTop="20px" hasBackground={false}>
 *   <Typography>Wrapped Content</Typography>
 * </BoxWrap>
 * ```
 * 
 * @remarks
 * - Uses Material-UI Box component
 * - Integrates with application theme
 * - Provides consistent border radius
 * - Supports theme-based background color
 * - Allows custom margin adjustments
 * - Passes through all Box props
 * 
 * @returns {JSX.Element} A React component that renders a styled box wrapper
 */
const BoxWrap: FunctionComponent<BoxWrapProps> = ({
  children,
  marginTop,
  hasBackground = true,
  ...props
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <>
      <Box
        borderRadius="8px"
        bgcolor={hasBackground ? colors.primary[400] : ""}
        marginTop={marginTop}
        {...props}
      >
        {children}
      </Box>
    </>
  );
};

export default BoxWrap;
